/**
 * Choosing an event, wherever an admin form needs one.
 *
 * Extracted because the speakers page's assign drawer had its own copy, built
 * from the code-defined `EVENTS` array alone — so every event created in the
 * admin CRM, which lives in `managedEvents`, was simply absent from the list.
 * An administrator trying to assign a speaker to an event they had made that
 * morning could not find it, and there was nothing on screen to explain why.
 *
 * Both sources, merged here once. Managed wins on a slug collision, matching
 * every other merge of these two in the codebase: the database row carries the
 * admin's edits, the array is the seed it grew from.
 *
 * Each option carries a summary — date, time, format, where — because a title
 * alone does not distinguish a recurring series. "Stop Answering the Same
 * Questions" has run more than once, and picking the wrong instance assigns a
 * speaker to the wrong night.
 */
import { useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { EVENTS } from "@/lib/eventsData";
import SearchableSelect, { type SearchableOption } from "@/components/SearchableSelect";

export type SelectableEvent = {
  slug: string;
  title: string;
  displayDate: string;
  time: string;
  location: string;
  type: "virtual" | "in-person";
  /** ISO date, for ordering and for splitting upcoming from past. */
  date: string;
};

/** The one-line summary shown under an event's title. */
export function describeEvent(event: SelectableEvent) {
  return [
    event.displayDate,
    event.time,
    event.type === "virtual" ? "Virtual" : "In-person",
    event.location,
  ]
    .map(part => String(part ?? "").trim())
    .filter(Boolean)
    .join(" · ");
}

/**
 * Every event an administrator can choose, upcoming first and each in date
 * order. Past events stay available — a speaker is sometimes recorded against a
 * talk that has already happened — but they sort below the ones still to come.
 */
export function useSelectableEvents() {
  const managedQuery = trpc.events.managedEvents.useQuery(undefined, { retry: false });

  return useMemo(() => {
    const managed = (managedQuery.data ?? []) as SelectableEvent[];
    const managedSlugs = new Set(managed.map(event => event.slug));
    const merged: SelectableEvent[] = [
      ...managed,
      ...(EVENTS as unknown as SelectableEvent[]).filter(event => !managedSlugs.has(event.slug)),
    ];

    const today = new Date().toISOString().slice(0, 10);
    const isUpcoming = (event: SelectableEvent) => String(event.date ?? "").slice(0, 10) >= today;
    const byDate = (left: SelectableEvent, right: SelectableEvent) =>
      String(left.date ?? "").localeCompare(String(right.date ?? ""));

    return {
      events: [
        ...merged.filter(isUpcoming).sort(byDate),
        // Most recent first among the past: a talk last month is far likelier to
        // be the one being recorded than one from two years ago.
        ...merged.filter(event => !isUpcoming(event)).sort((left, right) => byDate(right, left)),
      ],
      isLoading: managedQuery.isLoading,
    };
  }, [managedQuery.data, managedQuery.isLoading]);
}

export default function EventSelect({
  id,
  value,
  onChange,
  ariaLabel = "Event",
  placeholder = "Choose an event…",
  /** An explicit "no event" choice, for forms where the event is optional. */
  noneLabel,
}: {
  id: string;
  value: string | null;
  onChange: (slug: string | null) => void;
  ariaLabel?: string;
  placeholder?: string;
  noneLabel?: string;
}) {
  const { events, isLoading } = useSelectableEvents();

  const options: SearchableOption[] = useMemo(() => [
    ...(noneLabel ? [{ value: "", label: noneLabel }] : []),
    ...events.map(event => ({
      value: event.slug,
      label: event.title,
      hint: describeEvent(event),
    })),
  ], [events, noneLabel]);

  return (
    <SearchableSelect
      id={id}
      ariaLabel={ariaLabel}
      placeholder={isLoading ? "Loading events…" : placeholder}
      searchPlaceholder="Type an event name, date, or location…"
      emptyLabel="No event matches that search"
      value={value}
      onChange={onChange}
      options={options}
    />
  );
}
